<template>
	<div
		class="g-block-edit-shell"
		:class="{
			'is-active': active,
			'is-dragging': dragging,
			'is-drop-before': dropBefore,
			'is-drop-after': dropAfter,
		}"
		:data-block-id="blockId || undefined"
		@click.stop
	>
		<div v-if="draggable" class="g-block-edit-shell__toolbar g-block-edit-shell__toolbar--left">
			<button
				type="button"
				class="g-block-edit-shell__drag"
				@mousedown.stop.prevent="$emit('drag-start', $event)"
			>
				<span class="mbm-iconfont mbm-tuodong" style="font-size: 14px;"></span>
				<span>拖动</span>
			</button>
		</div>

		<div class="g-block-edit-shell__toolbar g-block-edit-shell__toolbar--right">
			<template v-for="(action, index) in textActions" :key="action.key">
				<span v-if="index > 0" class="g-block-edit-shell__divider"></span>
				<button
					type="button"
					class="g-block-edit-shell__action"
					@click.stop="$emit('action', action.key)"
				>{{ action.label }}</button>
			</template>
			<span v-if="textActions.length" class="g-block-edit-shell__divider"></span>
			<button
				type="button"
				class="g-block-edit-shell__action g-block-edit-shell__action--danger"
				@click.stop="$emit('delete')"
			>删除</button>
			<template v-if="hasColorSwatches">
				<span class="g-block-edit-shell__divider"></span>
				<div class="g-block-edit-shell__color-swatches">
					<div class="g-block-edit-shell__color-swatch">
						<ColorPicker
							:modelValue="gemColor"
							size="small"
							placement="top"
							@update:modelValue="val => onGemColorInput(val)"
						/>
					</div>
				</div>
			</template>
			<template v-else-if="hasGemColor">
				<span class="g-block-edit-shell__divider"></span>
				<div class="g-block-edit-shell__color-swatches">
					<div class="g-block-edit-shell__color-swatch">
						<ColorPicker
							:modelValue="gemColor"
							size="small"
							placement="top"
							@update:modelValue="val => onGemColorInput(val)"
						/>
					</div>
				</div>
			</template>
			<template v-else-if="hasDualColor">
				<span class="g-block-edit-shell__divider"></span>
				<div class="g-block-edit-shell__color-swatches">
					<div
						v-for="(color, colorIndex) in dualColors"
						:key="'dual-' + colorIndex"
						class="g-block-edit-shell__color-swatch"
					>
						<ColorPicker
							:modelValue="color"
							size="small"
							placement="top"
							@update:modelValue="val => onDualColorInput(colorIndex, val)"
						/>
					</div>
				</div>
			</template>
			<template v-else-if="hasSingleColor">
				<span class="g-block-edit-shell__divider"></span>
				<div class="g-block-edit-shell__color-swatch">
					<ColorPicker
						:modelValue="singleColor"
						size="small"
						placement="top"
						@update:modelValue="onSingleColorInput"
					/>
				</div>
			</template>
		</div>

		<div class="g-block-edit-shell__body">
			<slot />
		</div>
	</div>
</template>

<script>
	import { getBodyFrameIconColors, getGemColors, getTitleThemeColor } from '../templates.js';
	import { getSwatchBorderColor, normalizeHexColor } from '../utils/richText.js';
	import ColorPicker from './ColorPicker.vue';

	export default {
		name: 'GBlockEditShell',
		components: { ColorPicker },
		props: {
			active: {
				type: Boolean,
				default: false,
			},
			dragging: {
				type: Boolean,
				default: false,
			},
			dropBefore: {
				type: Boolean,
				default: false,
			},
			dropAfter: {
				type: Boolean,
				default: false,
			},
			actions: {
				type: Array,
				default: () => [],
			},
			blockData: {
				type: Object,
				default: () => ({}),
			},
			blockId: {
				type: String,
				default: '',
			},
			draggable: {
				type: Boolean,
				default: true,
			},
		},
		emits: ['drag-start', 'delete', 'action', 'gem-color-change', 'theme-color-change', 'dual-color-change'],
		computed: {
			textActions() {
				return this.actions.filter(action => !['color-swatches', 'color-picker', 'color-single', 'color-dual'].includes(action.type));
			},
			hasColorSwatches() {
				return this.actions.some(action => action.type === 'color-swatches');
			},
			hasGemColor() {
				return this.actions.some(action => action.type === 'color-picker');
			},
			hasDualColor() {
				return this.actions.some(action => action.type === 'color-dual');
			},
			hasSingleColor() {
				return this.actions.some(action => action.type === 'color-single');
			},
			dualColorAction() {
				return this.actions.find(action => action.type === 'color-dual');
			},
			dualColorLabels() {
				return this.dualColorAction?.labels || ['颜色1', '颜色2'];
			},
			gemColor() {
				const colors = getGemColors(this.blockData);
				return colors[0] || '#285af9';
			},
			dualColors() {
				return getBodyFrameIconColors(this.blockData);
			},
			singleColor() {
				return getTitleThemeColor(this.blockData);
			},
		},
		methods: {
			normalizeColor(color) {
				return normalizeHexColor(color);
			},
			getSwatchStyle(color) {
				const normalized = normalizeHexColor(color);
				return {
					background: normalized,
					borderColor: getSwatchBorderColor(normalized),
				};
			},
			onGemColorInput(color) {
				this.$emit('gem-color-change', { color });
			},
			onSingleColorInput(color) {
				this.$emit('theme-color-change', { color });
			},
			onDualColorInput(index, color) {
				this.$emit('dual-color-change', { index, color });
			},
		},
	};
</script>

<style scoped lang="less">
	.g-block-edit-shell {
		position: relative;
		box-sizing: border-box;
		border: 3px solid transparent;
		border-radius: 4px;
		transition: border-color 0.2s;

		&:hover,
		&.is-active {
			border-color: #285af9;
		}

		/* 编辑态描边仅由外壳承担，取消内部组件重复边框 */
		.g-block-edit-shell__body > :deep(.is-active) {
			border-color: transparent !important;
			box-shadow: none !important;
		}

		&.is-drop-before::before {
			content: '';
			position: absolute;
			top: -5px;
			left: 0;
			right: 0;
			height: 2px;
			background: #285af9;
			border-radius: 1px;
			pointer-events: none;
		}

		&.is-drop-after::after {
			content: '';
			position: absolute;
			bottom: -5px;
			left: 0;
			right: 0;
			height: 2px;
			background: #285af9;
			border-radius: 1px;
			pointer-events: none;
		}

		&.is-dragging {
			opacity: 0.45;
		}
	}

	.g-block-edit-shell__toolbar {
		position: absolute;
		top: -31px;
		z-index: 3;
		display: flex;
		align-items: center;
		min-height: 32px;
		opacity: 0;
		visibility: hidden;
		pointer-events: none;
		transition: opacity 0.15s, visibility 0.15s;

		&--left {
			left: 0;
		}

		&--right {
			right: 0;
			padding: 0 12px;
			background: #fff;
			border: 1px solid #eceef2;
			border-radius: 4px;
			box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
		}
	}

	.g-block-edit-shell:hover .g-block-edit-shell__toolbar,
	.g-block-edit-shell:focus-within .g-block-edit-shell__toolbar {
		opacity: 1;
		visibility: visible;
		pointer-events: auto;
	}

	.g-block-edit-shell.is-dragging .g-block-edit-shell__toolbar {
		opacity: 0;
		visibility: hidden;
		pointer-events: none;
	}

	.g-block-edit-shell__drag {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		height: 32px;
		padding: 0 10px;
		border: 1px solid #e8e8e8;
		border-radius: 4px;
		background: #fafafa;
		font-size: 13px;
		color: #666;
		cursor: grab;
		user-select: none;
		line-height: 1;

		&:active {
			cursor: grabbing;
		}

		&:hover {
			color: #285af9;
			border-color: #c9d8fd;
			background: #f5f8ff;
		}
	}

	.g-block-edit-shell__drag-icon {
		position: relative;
		width: 14px;
		height: 14px;
		flex-shrink: 0;

		&::before,
		&::after {
			content: '';
			position: absolute;
			left: 50%;
			top: 50%;
			background: currentColor;
			transform: translate(-50%, -50%);
		}

		&::before {
			width: 10px;
			height: 2px;
			box-shadow: 0 -4px 0 currentColor, 0 4px 0 currentColor;
		}

		&::after {
			width: 2px;
			height: 10px;
			box-shadow: -4px 0 0 currentColor, 4px 0 0 currentColor;
		}
	}

	.g-block-edit-shell__action {
		border: none;
		background: transparent;
		padding: 0 8px;
		font-size: 13px;
		color: #666;
		cursor: pointer;
		white-space: nowrap;
		line-height: 32px;

		&:hover {
			color: #285af9;
		}

		&--danger:hover {
			color: #ff4d4f;
		}
	}

	.g-block-edit-shell__divider {
		width: 1px;
		height: 16px;
		margin: 0 10px;
		background: #e8e8e8;
		flex-shrink: 0;
	}

	.g-block-edit-shell__color-swatches {
		display: inline-flex;
		align-items: center;
		gap: 4px;
	}

	.g-block-edit-shell__color-swatch {
		position: relative;
		display: inline-block;
		width: 16px;
		height: 16px;
		border: 1px solid transparent;
		border-radius: 3px;
		cursor: pointer;
		overflow: hidden;
		flex-shrink: 0;
		box-sizing: border-box;

		&:hover {
			box-shadow: 0 0 0 1px rgba(40, 90, 249, 0.25);
		}
	}

	.g-block-edit-shell__color-input {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		padding: 0;
		border: none;
		opacity: 0;
		cursor: pointer;
	}

	.g-block-edit-shell__body {
		min-width: 0;
	}
</style>
